import { useState, useEffect } from 'react';
import {
    FormHelperText, FormLabel, FormControl, Select, MenuItem,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
    AccordionSetup,
} from '@commonClient2/components';
import { UPDATE_GLOBAL_SETUP } from '../../store/globalSetup';
import DraggableCostCenterPill from './DraggableCostCenterPill';

import './OrgJobPathCostCenterConfig.scss';

const COST_CENTER_COUNT = 9;
const AVAILABLE_DELIMITERS = ['/', '|', '\\', '-', '_'];

/**
 * @param {{
 *  isDisabled?: boolean;
 *  isLoading: boolean;
 *  globalSetups: GlobalSetup.GlobalSetup;
 *  globalSetupsToReset: GlobalSetup.GlobalSetup;
 *  step: number;
 * }} param0
 * */
const OrgJobPathCostCenterConfig = ({
    isLoading,
    globalSetups,
    step,
}) => {
    const { t } = useTranslation('globalSetup', { keyPrefix: 'orgJobPathCostCenterConfig' });
    const dispatch = useDispatch();
    const { orgJobPathCostCenterConfig: config } = globalSetups;

    /** @type {ShiftBidding.ShiftBiddingStore} */
    // @ts-ignore
    const { publicConfigs } = useSelector((state) => state);
    const { isUseCostCenterAsOrgJobPath } = publicConfigs;

    const [selectedCostCenters, setSelectedCostCenters] = useState(config.selectedCostCenters || []);
    const [delimiter, setDelimiter] = useState(config.delimiter || '/');
    const [availableCostCenters, setAvailableCostCenters] = useState([]);

    useEffect(() => {
        setSelectedCostCenters(config.selectedCostCenters || []);
        setDelimiter(config.delimiter || '/');
    }, [JSON.stringify(config)]);

    useEffect(() => {
        const allCostCenters = Array.from({ length: COST_CENTER_COUNT }, (_, i) => (i + 1).toString());
        const available = allCostCenters.filter((cc) => !selectedCostCenters.includes(cc));
        setAvailableCostCenters(available);
    }, [selectedCostCenters]);

    /**
     * @template T
     * @param {T} value
     */
    const updateStore = (value) => {
        dispatch({
            type: UPDATE_GLOBAL_SETUP,
            payload: { orgJobPathCostCenterConfig: value },
        });
    };

    const handleAddCostCenter = (costCenter) => {
        const newSelected = [...selectedCostCenters, costCenter];
        setSelectedCostCenters(newSelected);
        updateStore({ selectedCostCenters: newSelected, delimiter });
    };

    const handleRemoveCostCenter = (index) => {
        const newSelected = selectedCostCenters.filter((_, i) => i !== index);
        setSelectedCostCenters(newSelected);
        updateStore({ selectedCostCenters: newSelected, delimiter });
    };

    const handleMoveCostCenter = (dragIndex, hoverIndex) => {
        setSelectedCostCenters((prevSelected) => {
            const newSelected = [...prevSelected];
            const [removed] = newSelected.splice(dragIndex, 1);
            newSelected.splice(hoverIndex, 0, removed);
            updateStore({ selectedCostCenters: newSelected, delimiter });
            return newSelected;
        });
    };

    const handleDelimiterChange = (newDelimiter) => {
        setDelimiter(newDelimiter);
        updateStore({ selectedCostCenters, delimiter: newDelimiter });
    };

    // Only render if using cost centers as org job path
    if (!isUseCostCenterAsOrgJobPath) {
        return null;
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <AccordionSetup
                stepIndex={step.toString()}
                headerTitle={t('title')}
                summary=""
                className="orgjobpath-cost-center-config-step"
            >
                <div className="orgjobpath-cost-center-config">
                    <FormControl fullWidth>
                        <FormLabel>{t('description')}</FormLabel>
                        <FormHelperText>{t('helperText')}</FormHelperText>
                    </FormControl>

                    <div className="cost-center-selection">
                        <FormControl fullWidth className="add-cost-center-dropdown">
                            <FormLabel>{t('addCostCenter')}</FormLabel>
                            <Select
                                value=""
                                onChange={(e) => handleAddCostCenter(e.target.value)}
                                disabled={availableCostCenters.length === 0}
                                displayEmpty
                            >
                                <MenuItem value="" disabled>
                                    {availableCostCenters.length === 0
                                        ? t('allCostCentersSelected')
                                        : t('selectCostCenter')}
                                </MenuItem>
                                {availableCostCenters.map((cc) => (
                                    <MenuItem key={cc} value={cc}>
                                        {t('costCenterLabel', { number: cc })}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth className="delimiter-selector">
                            <FormLabel>{t('delimiter')}</FormLabel>
                            <Select
                                value={delimiter}
                                onChange={(e) => handleDelimiterChange(e.target.value)}
                            >
                                {AVAILABLE_DELIMITERS.map((delim) => (
                                    <MenuItem key={delim} value={delim}>
                                        {delim}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div className="selected-cost-centers">
                        <FormLabel>{t('selectedCostCenters')}</FormLabel>
                        {selectedCostCenters.length === 0 ? (
                            <FormHelperText>{t('noCostCentersSelected')}</FormHelperText>
                        ) : (
                            <div className="cost-center-pills">
                                {selectedCostCenters.map((cc, index) => (
                                    <DraggableCostCenterPill
                                        key={cc}
                                        index={index}
                                        costCenter={cc}
                                        onMove={handleMoveCostCenter}
                                        onRemove={handleRemoveCostCenter}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedCostCenters.length > 0 && (
                        <div className="preview">
                            <FormLabel>{t('preview')}</FormLabel>
                            <FormHelperText>
                                {selectedCostCenters.map((cc) => t('costCenterLabel', { number: cc })).join(` ${delimiter} `)}
                            </FormHelperText>
                        </div>
                    )}
                </div>
            </AccordionSetup>
        </DndProvider>
    );
};

export default OrgJobPathCostCenterConfig;

